import { ISelectOption, Option } from "components/Select/Select";
import { useState, useCallback, ReactNode } from "react";

type IGroupOption<T> = { label: ReactNode, options: ISelectOption<T>[] }
export interface IUseSelectOptimizationProps<T> {
    options?: ISelectOption<T>[]
    size?: number,
    filterOption?: (option: Option, search: string) => boolean
    groupOptions?: (options: ISelectOption<T>[]) => IGroupOption<T>[]
}

interface ISearchDataState<T> {
    currentSearch?: string,
    options: ISelectOption<T>[],
    page: number,
    pagesCount: number,
    hasMore: boolean,
}

interface GetGroupedOptionsPageParamsType<T> {
    allOptions: ISelectOption<T>[],
    page: number,
    size: number,
    groupOptions(options: ISelectOption<T>[]): IGroupOption<T>[]
}

function getGroupedOptionsPage<T>({ allOptions, page, size, groupOptions }: GetGroupedOptionsPageParamsType<T>): (IGroupOption<T> | ISelectOption<T>)[] {
    //find options
    const flatOptions = allOptions.slice(page * size, page * size + size)
    //group Options
    const groupedOptions = groupOptions(flatOptions).filter(group => group.options.length > 0)
    // check if first item of new page is part of last page last group don't show group label
    if (groupedOptions[0]?.options[0].group === allOptions[page * size - 1].group) {
        const [firstGroup, ...otherGroups] = groupedOptions;
        return [...firstGroup.options, ...otherGroups]
    } else return groupedOptions


}

export default function useSelectOptimization<T = any>({ options = [], size = 100, filterOption, groupOptions }: IUseSelectOptimizationProps<T>) {
    const [searchData, setSearchData] = useState<ISearchDataState<T>>({ options: [], page: 0, pagesCount: 0, hasMore: false })

    const getOptions = useCallback((search: string) => {
        return new Promise((resolve: (any) => void) => {
            if (searchData.currentSearch === undefined || search !== searchData.currentSearch) {
                let searchedOptions = filterOption ?
                    options.filter(option => filterOption({ data: option } as Option, search))
                    : options.filter(item => item.label.search(RegExp(search, "i")) !== -1)
                const pagesCount = Math.ceil(searchedOptions.length / size)
                // sorting items according groups
                if (groupOptions) {
                    searchedOptions = groupOptions(searchedOptions || []).filter(group => group.options.length > 0).reduce((accum, group) => ([...accum, ...group.options]), [] as ISelectOption<T>[])
                }
                const hasMore = pagesCount > 1

                setSearchData(prevSearchData => ({
                    ...prevSearchData,
                    options: searchedOptions,
                    currentSearch: search,
                    page: 0,
                    pagesCount,
                    hasMore
                }))
                resolve({
                    options: groupOptions ? groupOptions(searchedOptions.slice(0, size) || []).filter(group => group.options.length > 0) : searchedOptions.slice(0, size),
                    hasMore
                })
            } else {
                const currentPage = searchData.page + 1
                const hasMore = currentPage < (searchData.pagesCount - 1) //decrement pages count because of starting from 0
                setSearchData(prevSearchData => ({
                    ...prevSearchData,
                    page: currentPage,
                    hasMore
                }))
                resolve({
                    options: groupOptions ?
                        getGroupedOptionsPage({ allOptions: searchData.options, page: currentPage, size, groupOptions }) :
                        searchData.options.slice(currentPage * size, currentPage * size + size),
                    hasMore
                })
            }
        }
        )
    }, [searchData.currentSearch, searchData.page, searchData.pagesCount, searchData.options, filterOption, options, size, groupOptions])

    return {
        getOptions,
        hasMore: searchData.hasMore
    }
}
